<script lang="ts">
  import { onDestroy } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { mdiImagePlus } from "@mdi/js"
  import { previewLetterSignaturePhoto } from "../../../../../../../../../../../../../../../../api/kehrblatt/contact-policy"
  import type { SignaturePhotoUploadCompleted } from "../../_shared/_logic/signature-contract"
  import { signaturePhotoUploadErrorMessage } from "./_logic/signaturePhotoUploadErrors"

  const { organizationId, onUploaded } = $props<{
    organizationId: string
    onUploaded: SignaturePhotoUploadCompleted
  }>()

  let fileInput = $state<HTMLInputElement | null>(null)
  let pending = $state(false)
  let active = true
  onDestroy(() => {
    active = false
  })

  const selectPhoto = () => {
    if (!pending) fileInput?.click()
  }

  const uploadPhoto = async (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ""

    if (!file || pending) return

    pending = true
    try {
      const preview = await previewLetterSignaturePhoto(organizationId, file)

      if (!active) return

      onUploaded({
        image_png_base64: preview.image_base64,
        width_px: preview.width,
        height_px: preview.height,
      })
      newNotification({
        class: "success",
        html: "Unterschrift wurde als Entwurf übernommen. Bitte speichern Sie anschließend auf der Seite.",
      })
    } catch (error) {
      if (!active) return

      newNotification({ class: "error", html: signaturePhotoUploadErrorMessage(error) })
    } finally {
      pending = false
    }
  }
</script>

<section class="photo-upload" aria-labelledby="signature-photo-heading">
  <div>
    <h3 id="signature-photo-heading">Unterschrift fotografieren</h3>
    <p>
      Fotografieren Sie Ihre Unterschrift mit einem dunklen Stift auf weißem Papier. Achten Sie auf
      ein scharfes, möglichst gerades Bild ohne Schatten.
    </p>
  </div>
  <CTA
    type="secondaryButton"
    content={pending ? "Foto wird verarbeitet…" : "Foto hochladen"}
    onClick={selectPhoto}
    disabled={pending}
  >
    {#snippet iconSnippet()}
      <Icon path={mdiImagePlus} />
    {/snippet}
  </CTA>
  <input
    class="photo-input"
    bind:this={fileInput}
    type="file"
    accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif"
    aria-label="Foto der Unterschrift auswählen"
    onchange={uploadPhoto}
  />
</section>

<style lang="less">
  .photo-upload {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
    border: 1px solid var(--text-invers-60);

    h3,
    p {
      margin: 0;
    }

    h3 {
      margin-bottom: 0.35rem;
      font-size: 1rem;
    }
  }

  .photo-input {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }

  @media (max-width: 767px) {
    .photo-upload {
      align-items: stretch;
      flex-direction: column;
    }
  }
</style>
